<template>
  <div class="modal fade show d-block" tabindex="-1" style="background-color: rgba(0,0,0,0.5)">
    <div class="modal-dialog modal-xl modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">Edit Household Information</h5>
          <button type="button" class="btn-close" @click="$emit('close')"></button>
        </div>
        <div class="modal-body">
          <!-- Loading State -->
          <div v-if="loading" class="text-center py-5">
            <div class="spinner-border text-primary" role="status">
              <span class="visually-hidden">Loading...</span>
            </div>
            <p class="mt-3">Loading household details...</p>
          </div>

          <!-- Form Content -->
          <div v-else>
            <!-- Nav Tabs -->
            <ul class="nav nav-tabs nav-tabs-custom nav-justified" role="tablist">
              <li class="nav-item">
                <a class="nav-link" :class="{'active': activeTab === 'basic'}"
                   @click="activeTab = 'basic'" href="javascript:void(0);" role="tab">
                  <i class="bx bx-home-circle"></i> Basic Information
                </a>
              </li>
              <li class="nav-item">
                <a class="nav-link" :class="{'active': activeTab === 'location'}"
                   @click="activeTab = 'location'" href="javascript:void(0);" role="tab">
                  <i class="bx bx-map"></i> Location
                </a>
              </li>
              <li class="nav-item">
                <a class="nav-link" :class="{'active': activeTab === 'environment'}"
                   @click="activeTab = 'environment'" href="javascript:void(0);" role="tab">
                  <i class="bx bx-home-alt"></i> Living Conditions
                </a>
              </li>
              <li class="nav-item">
                <a class="nav-link" :class="{'active': activeTab === 'status'}"
                   @click="activeTab = 'status'" href="javascript:void(0);" role="tab">
                  <i class="bx bx-shield"></i> Special Status
                </a>
              </li>
            </ul>

            <!-- Tab Content -->
            <div class="tab-content p-3 text-muted">
              <!-- Basic Information Tab -->
              <div v-show="activeTab === 'basic'" class="tab-pane active">
                <div class="row">
                  <div class="col-md-6 mb-3">
                    <label class="form-label">Household Number</label>
                    <input v-model="form.householdNumber" type="text" class="form-control" readonly>
                  </div>

                  <div class="col-md-6 mb-3">
                    <label class="form-label">Registration Date</label>
                    <input v-model="form.registrationDate" type="date" class="form-control" readonly>
                  </div>
                </div>

                <div class="row">
                  <div class="col-md-6 mb-3">
                    <label class="form-label">Head of Household</label>
                    <select v-model="form.householdHeadId" class="form-select">
                      <option value="">Select household head</option>
                      <option v-for="member in householdMembers" :key="member.member_id"
                              :value="member.member_id">
                        {{ member.first_name }} {{ member.surname }}
                        ({{ member.relationship_to_head }})
                      </option>
                    </select>
                  </div>

                  <div class="col-md-6 mb-3">
                    <label class="form-label">Monthly Income (ETB)</label>
                    <input v-model="form.monthlyIncome" type="number" class="form-control"
                           placeholder="Estimated monthly income">
                  </div>
                </div>

                <div class="row">
                  <div class="col-md-6 mb-3">
                    <label class="form-label">Primary Language</label>
                    <select v-model="form.primaryLanguage" class="form-select">
                      <option value="">Select language</option>
                      <option value="amharic">Amharic</option>
                      <option value="oromo">Afaan Oromo</option>
                      <option value="tigrinya">Tigrinya</option>
                      <option value="somali">Somali</option>
                      <option value="sidama">Sidamo</option>
                      <option value="other">Other</option>
                    </select>
                  </div>

                  <div class="col-md-6 mb-3">
                    <label class="form-label">Religion</label>
                    <select v-model="form.religion" class="form-select">
                      <option value="">Select religion</option>
                      <option value="orthodox">Orthodox Christian</option>
                      <option value="muslim">Muslim</option>
                      <option value="protestant">Protestant</option>
                      <option value="catholic">Catholic</option>
                      <option value="traditional">Traditional</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                </div>

                <div class="mb-3">
                  <label class="form-label">Notes/Comments</label>
                  <textarea v-model="form.notes" class="form-control" rows="3"
                            placeholder="Any additional notes about this household..."></textarea>
                </div>
              </div>

              <!-- Location Tab -->
              <div v-show="activeTab === 'location'" class="tab-pane">
                <div class="row">
                  <div class="col-md-6 mb-3">
                    <label class="form-label">Region</label>
                    <select v-model="form.regionId" @change="loadZones" class="form-select" disabled>
                      <option :value="form.regionId">{{ form.regionName }}</option>
                    </select>
                  </div>

                  <div class="col-md-6 mb-3">
                    <label class="form-label">Zone</label>
                    <select v-model="form.zoneId" @change="loadWoredas" class="form-select" disabled>
                      <option :value="form.zoneId">{{ form.zoneName }}</option>
                    </select>
                  </div>
                </div>

                <div class="row">
                  <div class="col-md-6 mb-3">
                    <label class="form-label">Woreda</label>
                    <select v-model="form.woredaId" @change="loadKebeles" class="form-select" disabled>
                      <option :value="form.woredaId">{{ form.woredaName }}</option>
                    </select>
                  </div>

                  <div class="col-md-6 mb-3">
                    <label class="form-label">Kebele</label>
                    <select v-model="form.kebeleId" class="form-select" disabled>
                      <option :value="form.kebeleId">{{ form.kebeleName }}</option>
                    </select>
                  </div>
                </div>

                <div class="row">
                  <div class="col-md-6 mb-3">
                    <label class="form-label">Village/Gote</label>
                    <input v-model="form.village" type="text" class="form-control"
                           placeholder="Village or Gote name">
                  </div>

                  <div class="col-md-6 mb-3">
                    <label class="form-label">House Number</label>
                    <input v-model="form.houseNumber" type="text" class="form-control"
                           placeholder="Physical house number">
                  </div>
                </div>

                <div class="row">
                  <div class="col-12 mb-3">
                    <label class="form-label">GPS Coordinates</label>
                    <div class="input-group">
                      <input v-model="form.gpsLatitude" type="number" step="0.000001"
                             class="form-control" placeholder="Latitude">
                      <input v-model="form.gpsLongitude" type="number" step="0.000001"
                             class="form-control" placeholder="Longitude">
                      <button @click="captureGPS" type="button" class="btn btn-outline-primary"
                              :disabled="gpsLoading">
                        <span v-if="gpsLoading">
                          <i class="bx bx-loader-alt bx-spin"></i> Getting location...
                        </span>
                        <span v-else>
                          <i class="bx bx-current-location"></i> Get Current Location
                        </span>
                      </button>
                    </div>
                    <small v-if="gpsError" class="text-danger">{{ gpsError }}</small>
                  </div>
                </div>

                <div class="mb-3">
                  <label class="form-label">Landmark/Directions</label>
                  <textarea v-model="form.landmark" class="form-control" rows="2"
                            placeholder="Nearby landmarks or directions to the house..."></textarea>
                </div>
              </div>

              <!-- Living Conditions Tab -->
              <div v-show="activeTab === 'environment'" class="tab-pane">
                <div class="row">
                  <div class="col-md-6 mb-3">
                    <label class="form-label">House Type</label>
                    <select v-model="form.houseType" class="form-select">
                      <option value="">Select house type</option>
                      <option value="mud_thatched">Mud wall with thatched roof</option>
                      <option value="mud_corrugated">Mud wall with corrugated iron</option>
                      <option value="brick_corrugated">Brick/Block with corrugated iron</option>
                      <option value="modern">Modern house</option>
                      <option value="other">Other</option>
                    </select>
                  </div>

                  <div class="col-md-6 mb-3">
                    <label class="form-label">Number of Rooms</label>
                    <input v-model="form.numberOfRooms" type="number" class="form-control"
                           min="1" placeholder="Total rooms">
                  </div>
                </div>

                <div class="row">
                  <div class="col-md-6 mb-3">
                    <label class="form-label">Water Source</label>
                    <select v-model="form.waterSource" class="form-select">
                      <option value="">Select water source</option>
                      <option value="piped_home">Piped water in home</option>
                      <option value="piped_yard">Piped water in yard</option>
                      <option value="public_tap">Public tap/standpipe</option>
                      <option value="protected_well">Protected well</option>
                      <option value="unprotected_well">Unprotected well</option>
                      <option value="spring">Spring</option>
                      <option value="river">River/stream</option>
                      <option value="other">Other</option>
                    </select>
                  </div>

                  <div class="col-md-6 mb-3">
                    <label class="form-label">Toilet/Latrine Type</label>
                    <select v-model="form.latrineType" class="form-select">
                      <option value="">Select latrine type</option>
                      <option value="flush_sewage">Flush to sewage system</option>
                      <option value="flush_septic">Flush to septic tank</option>
                      <option value="ventilated_pit">Ventilated improved pit</option>
                      <option value="pit_with_slab">Pit latrine with slab</option>
                      <option value="pit_without_slab">Pit latrine without slab</option>
                      <option value="none">No facility/Bush/Field</option>
                    </select>
                  </div>
                </div>

                <div class="row">
                  <div class="col-md-6 mb-3">
                    <label class="form-label">Waste Disposal Method</label>
                    <select v-model="form.wasteDisposal" class="form-select">
                      <option value="">Select method</option>
                      <option value="collected">Collected by authorities</option>
                      <option value="burned">Burned</option>
                      <option value="buried">Buried</option>
                      <option value="dumped">Dumped in designated area</option>
                      <option value="open">Open dumping</option>
                      <option value="other">Other</option>
                    </select>
                  </div>

                  <div class="col-md-6 mb-3">
                    <label class="form-label">Main Cooking Fuel</label>
                    <select v-model="form.cookingFuel" class="form-select">
                      <option value="">Select fuel type</option>
                      <option value="electricity">Electricity</option>
                      <option value="gas">LPG/Natural gas</option>
                      <option value="kerosene">Kerosene</option>
                      <option value="charcoal">Charcoal</option>
                      <option value="wood">Wood</option>
                      <option value="animal_dung">Animal dung</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                </div>

                <div class="row">
                  <div class="col-12">
                    <label class="form-label">Household Assets</label>
                    <div class="row">
                      <div class="col-md-3 mb-2">
                        <div class="form-check">
                          <input v-model="form.hasElectricity" class="form-check-input"
                                 type="checkbox" id="electricityCheck">
                          <label class="form-check-label" for="electricityCheck">
                            Electricity
                          </label>
                        </div>
                      </div>
                      <div class="col-md-3 mb-2">
                        <div class="form-check">
                          <input v-model="form.hasRadio" class="form-check-input"
                                 type="checkbox" id="radioCheck">
                          <label class="form-check-label" for="radioCheck">
                            Radio
                          </label>
                        </div>
                      </div>
                      <div class="col-md-3 mb-2">
                        <div class="form-check">
                          <input v-model="form.hasTelevision" class="form-check-input"
                                 type="checkbox" id="televisionCheck">
                          <label class="form-check-label" for="televisionCheck">
                            Television
                          </label>
                        </div>
                      </div>
                      <div class="col-md-3 mb-2">
                        <div class="form-check">
                          <input v-model="form.hasMobile" class="form-check-input"
                                 type="checkbox" id="mobileCheck">
                          <label class="form-check-label" for="mobileCheck">
                            Mobile Phone
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Special Status Tab -->
              <div v-show="activeTab === 'status'" class="tab-pane">
                <div class="row">
                  <div class="col-12 mb-4">
                    <h6>Household Classifications</h6>
                    <div class="row">
                      <div class="col-md-6 mb-3">
                        <div class="form-check form-switch">
                          <input v-model="form.isFemaleHeaded" class="form-check-input"
                                 type="checkbox" id="femaleHeadedCheck">
                          <label class="form-check-label" for="femaleHeadedCheck">
                            Female Headed Household
                          </label>
                        </div>
                      </div>
                      <div class="col-md-6 mb-3">
                        <div class="form-check form-switch">
                          <input v-model="form.isVulnerable" class="form-check-input"
                                 type="checkbox" id="vulnerableCheck">
                          <label class="form-check-label" for="vulnerableCheck">
                            Vulnerable Household
                          </label>
                        </div>
                      </div>
                      <div class="col-md-6 mb-3">
                        <div class="form-check form-switch">
                          <input v-model="form.isModelHousehold" class="form-check-input"
                                 type="checkbox" id="modelCheck">
                          <label class="form-check-label" for="modelCheck">
                            Model Household
                          </label>
                        </div>
                      </div>
                      <div class="col-md-6 mb-3">
                        <div class="form-check form-switch">
                          <input v-model="form.hasDisabledMember" class="form-check-input"
                                 type="checkbox" id="disabledCheck">
                          <label class="form-check-label" for="disabledCheck">
                            Has Disabled Member(s)
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div v-if="form.isVulnerable" class="col-12 mb-3">
                    <label class="form-label">Vulnerability Reasons</label>
                    <textarea v-model="form.vulnerabilityReasons" class="form-control" rows="3"
                              placeholder="Describe reasons for vulnerability classification..."></textarea>
                  </div>

                  <div v-if="form.isModelHousehold" class="col-12 mb-3">
                    <h6>Model Household Criteria</h6>
                    <div class="row">
                      <div class="col-md-6">
                        <div class="form-check">
                          <input v-model="form.modelCriteria.hasLatrine" class="form-check-input"
                                 type="checkbox" id="modelLatrineCheck">
                          <label class="form-check-label" for="modelLatrineCheck">
                            Has proper latrine
                          </label>
                        </div>
                        <div class="form-check">
                          <input v-model="form.modelCriteria.hasHandwashing" class="form-check-input"
                                 type="checkbox" id="modelHandwashingCheck">
                          <label class="form-check-label" for="modelHandwashingCheck">
                            Has handwashing facility
                          </label>
                        </div>
                        <div class="form-check">
                          <input v-model="form.modelCriteria.hasSeparateKitchen" class="form-check-input"
                                 type="checkbox" id="modelKitchenCheck">
                          <label class="form-check-label" for="modelKitchenCheck">
                            Has separate kitchen
                          </label>
                        </div>
                      </div>
                      <div class="col-md-6">
                        <div class="form-check">
                          <input v-model="form.modelCriteria.hasWasteDisposal" class="form-check-input"
                                 type="checkbox" id="modelWasteCheck">
                          <label class="form-check-label" for="modelWasteCheck">
                            Proper waste disposal
                          </label>
                        </div>
                        <div class="form-check">
                          <input v-model="form.modelCriteria.hasSafeWaterStorage" class="form-check-input"
                                 type="checkbox" id="modelWaterCheck">
                          <label class="form-check-label" for="modelWaterCheck">
                            Safe water storage
                          </label>
                        </div>
                        <div class="form-check">
                          <input v-model="form.modelCriteria.practicesHealthBehaviors" class="form-check-input"
                                 type="checkbox" id="modelBehaviorsCheck">
                          <label class="form-check-label" for="modelBehaviorsCheck">
                            Practices health behaviors
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" @click="$emit('close')">Cancel</button>
          <button type="button" class="btn btn-primary" @click="updateHousehold"
                  :disabled="submitting">
            <span v-if="submitting">
              <span class="spinner-border spinner-border-sm me-1"></span> Updating...
            </span>
            <span v-else>
              <i class="bx bx-save"></i> Save Changes
            </span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import { authUrl, executeJson } from "@/api";

export default {
  name: "EditHouseholdModal",
  props: {
    household: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      loading: false,
      submitting: false,
      activeTab: 'basic',

      // Household members for head selection
      householdMembers: [],

      // GPS
      gpsLoading: false,
      gpsError: null,

      // Form data
      form: {
        // Basic
        householdId: null,
        householdNumber: '',
        registrationDate: '',
        householdHeadId: null,
        monthlyIncome: null,
        primaryLanguage: '',
        religion: '',
        notes: '',

        // Location
        regionId: null,
        regionName: '',
        zoneId: null,
        zoneName: '',
        woredaId: null,
        woredaName: '',
        kebeleId: null,
        kebeleName: '',
        village: '',
        houseNumber: '',
        gpsLatitude: null,
        gpsLongitude: null,
        landmark: '',

        // Living conditions
        houseType: '',
        numberOfRooms: null,
        waterSource: '',
        latrineType: '',
        wasteDisposal: '',
        cookingFuel: '',
        hasElectricity: false,
        hasRadio: false,
        hasTelevision: false,
        hasMobile: false,

        // Special status
        isFemaleHeaded: false,
        isVulnerable: false,
        isModelHousehold: false,
        hasDisabledMember: false,
        vulnerabilityReasons: '',

        // Model household criteria
        modelCriteria: {
          hasLatrine: false,
          hasHandwashing: false,
          hasSeparateKitchen: false,
          hasWasteDisposal: false,
          hasSafeWaterStorage: false,
          practicesHealthBehaviors: false
        }
      }
    };
  },

  mounted() {
    this.loadHouseholdDetails();
  },

  methods: {
    async loadHouseholdDetails() {
      this.loading = true;

      const data = {
        "function": "getHouseholdDetails",
        "householdId": this.household.household_id
      };

      try {
        const res = await executeJson(data, authUrl);

        if (res.data.success) {
          const household = res.data.data.household;
          const members = res.data.data.members || [];

          this.householdMembers = members;

          // Populate form with existing data
          this.form = {
            householdId: household.household_id,
            householdNumber: household.household_number,
            registrationDate: household.registration_date?.split(' ')[0],
            householdHeadId: household.household_head_id,
            monthlyIncome: household.monthly_income,
            primaryLanguage: household.primary_language || '',
            religion: household.religion || '',
            notes: household.notes || '',

            // Location
            regionId: household.region_id,
            regionName: household.region_name,
            zoneId: household.zone_id,
            zoneName: household.zone_name,
            woredaId: household.woreda_id,
            woredaName: household.woreda_name,
            kebeleId: household.kebele_id,
            kebeleName: household.kebele_name,
            village: household.village || '',
            houseNumber: household.house_number || '',
            gpsLatitude: household.gps_latitude,
            gpsLongitude: household.gps_longitude,
            landmark: household.landmark || '',

            // Living conditions
            houseType: household.house_type || '',
            numberOfRooms: household.number_of_rooms,
            waterSource: household.water_source || '',
            latrineType: household.latrine_type || '',
            wasteDisposal: household.waste_disposal_method || '',
            cookingFuel: household.cooking_fuel || '',
            hasElectricity: household.electricity_access || false,
            hasRadio: household.has_radio || false,
            hasTelevision: household.has_television || false,
            hasMobile: household.has_mobile || false,

            // Special status
            isFemaleHeaded: household.is_female_headed || false,
            isVulnerable: household.is_vulnerable || false,
            isModelHousehold: household.is_model_household || false,
            hasDisabledMember: household.has_disabled_member || false,
            vulnerabilityReasons: household.vulnerability_reasons || '',

            // Model household criteria - will be loaded if exists
            modelCriteria: {
              hasLatrine: false,
              hasHandwashing: false,
              hasSeparateKitchen: false,
              hasWasteDisposal: false,
              hasSafeWaterStorage: false,
              practicesHealthBehaviors: false
            }
          };

          // Load model household criteria if applicable
          if (household.is_model_household && res.data.data.modelCriteria) {
            const criteria = res.data.data.modelCriteria;
            this.form.modelCriteria = {
              hasLatrine: criteria.has_latrine || false,
              hasHandwashing: criteria.has_handwashing_facility || false,
              hasSeparateKitchen: criteria.has_separate_kitchen || false,
              hasWasteDisposal: criteria.has_waste_disposal || false,
              hasSafeWaterStorage: criteria.safe_water_storage || false,
              practicesHealthBehaviors: criteria.practices_health_behaviors || false
            };
          }
        }
      } catch (e) {
        console.error("Failed to load household details:", e);
        alert('Failed to load household details');
      } finally {
        this.loading = false;
      }
    },

    async captureGPS() {
      if (!navigator.geolocation) {
        this.gpsError = "Geolocation is not supported by your browser";
        return;
      }

      this.gpsLoading = true;
      this.gpsError = null;

      navigator.geolocation.getCurrentPosition(
          (position) => {
            this.form.gpsLatitude = position.coords.latitude;
            this.form.gpsLongitude = position.coords.longitude;
            this.gpsLoading = false;
          },
          (error) => {
            this.gpsLoading = false;
            switch(error.code) {
              case error.PERMISSION_DENIED:
                this.gpsError = "Location permission denied";
                break;
              case error.POSITION_UNAVAILABLE:
                this.gpsError = "Location information unavailable";
                break;
              case error.TIMEOUT:
                this.gpsError = "Location request timed out";
                break;
              default:
                this.gpsError = "An unknown error occurred";
            }
          },
          {
            enableHighAccuracy: true,
            timeout: 10000,
            maximumAge: 0
          }
      );
    },

    async updateHousehold() {
      this.submitting = true;

      const data = {
        "function": "updateHousehold",
        "householdId": this.form.householdId,
        "householdHeadId": this.form.householdHeadId,
        "monthlyIncome": this.form.monthlyIncome,
        "primaryLanguage": this.form.primaryLanguage,
        "religion": this.form.religion,
        "notes": this.form.notes,
        "village": this.form.village,
        "houseNumber": this.form.houseNumber,
        "gpsLatitude": this.form.gpsLatitude,
        "gpsLongitude": this.form.gpsLongitude,
        "landmark": this.form.landmark,
        "houseType": this.form.houseType,
        "numberOfRooms": this.form.numberOfRooms,
        "waterSource": this.form.waterSource,
        "latrineType": this.form.latrineType,
        "wasteDisposal": this.form.wasteDisposal,
        "cookingFuel": this.form.cookingFuel,
        "hasElectricity": this.form.hasElectricity,
        "hasRadio": this.form.hasRadio,
        "hasTelevision": this.form.hasTelevision,
        "hasMobile": this.form.hasMobile,
        "isFemaleHeaded": this.form.isFemaleHeaded,
        "isVulnerable": this.form.isVulnerable,
        "isModelHousehold": this.form.isModelHousehold,
        "hasDisabledMember": this.form.hasDisabledMember,
        "vulnerabilityReasons": this.form.vulnerabilityReasons,
        "modelCriteria": this.form.isModelHousehold ? this.form.modelCriteria : null
      };

      try {
        const res = await executeJson(data, authUrl);

        if (res.data.success) {
          this.$emit('updated');
        } else {
          alert(res.data.message || 'Failed to update household');
        }
      } catch (e) {
        console.error("Failed to update household:", e);
        alert('Failed to update household. Please try again.');
      } finally {
        this.submitting = false;
      }
    },

    // Location methods (currently disabled as kebele is fixed)
    loadZones() {
      // Implement if needed
    },

    loadWoredas() {
      // Implement if needed
    },

    loadKebeles() {
      // Implement if needed
    }
  }
};
</script>

<style scoped>
.modal {
  display: block;
}

.nav-tabs .nav-link {
  cursor: pointer;
  color: #495057;
}

.nav-tabs .nav-link.active {
  color: #556ee6;
}

.nav-tabs .nav-link i {
  margin-right: 5px;
}

.spinner-border-sm {
  width: 1rem;
  height: 1rem;
  border-width: 0.15em;
}

.bx-spin {
  animation: spin 2s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.form-check-input {
  cursor: pointer;
}

.form-switch .form-check-input {
  width: 3em;
}
</style>